import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, CircularProgress, makeStyles } from '@material-ui/core';
import clsx from 'clsx';

import { Eye } from '~/views/private/overview/Components/icons/Eye';
import { Download } from '~/views/private/overview/Components/icons/Download';
import { Panorama } from '~/views/private/overview/Components/icons/Panorama';
import { ArrowsNav } from '~/views/private/overview/ContentEditor/icons/ArrowsNav';
import { Trash } from '~/views/private/overview/ContentEditor/icons/Trash';

const useStyles = makeStyles(() => ({
  row: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: 216,
    height: 160,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  grabbable: {
    cursor: 'grab',
  },
  dragging: {
    opacity: 0.55,
    cursor: 'grabbing',
  },
  dragHandle: {
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: 1,
    display: 'flex',
    color: 'var(--content-primary)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    width: 216,
    height: 160,
    minWidth: 0,
    boxSizing: 'border-box',
    overflow: 'visible',
    background: 'var(--surface-default, #FFFFFF)',
    border: '1px solid var(--border-default, #171C261A)',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
  },
  actions: {
    position: 'absolute',
    left: 4,
    bottom: 4,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
  },
  actionButton: {
    width: 48,
    height: 32,
    border: 'none',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    background: 'var(--surface-accent, #5E59F3)',
    color: 'var(--content-inverted, #FFF)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default',
    },
  },
  deleteAnchor: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    display: 'flex',
    zIndex: 1,
  },
  deleteButton: {
    width: 48,
    height: 32,
    border: 'none',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    background: 'var(--surface-danger, #E5484D)',
    color: 'var(--content-inverted, #FFF)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default',
    },
  },
  confirm: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    zIndex: 50,
    minWidth: 240,
    maxWidth: 280,
    padding: 16,
    boxSizing: 'border-box',
    background: 'var(--surface-default, #FFFFFF)',
    border: '1px solid var(--border-default, #171C261A)',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    boxShadow: 'var(--shadows-popover)',
  },
  confirmMessage: {
    marginBottom: 16,
    color: 'var(--content-primary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontWeight: 400,
    lineHeight: 'var(--typography-line-height-sm-line)',
  },
  confirmActions: {
    display: 'flex',
    gap: 8,
  },
  confirmButton: {
    flex: '1 1 0',
    minWidth: 0,
    height: 36,
    border: 'none',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    cursor: 'pointer',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontWeight: 500,
  },
  cancelButton: {
    background: 'var(--surface-tint-weak, #171C260D)',
    color: 'var(--content-primary)',
  },
  confirmDeleteButton: {
    background: 'var(--surface-danger, #E5484D)',
    color: 'var(--content-inverted, #FFF)',
  },
  mediaWrap: {
    position: 'absolute',
    inset: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: 4,
    boxSizing: 'border-box',
  },
  media: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  mediaHidden: {
    opacity: 0,
  },
  loader: {
    position: 'absolute',
    inset: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    background: 'var(--surface-tint-weak, #171C260D)',
    color: 'var(--content-secondary)',
  },
  icon: {
    width: 16,
    height: 16,
  },
}));

export const ContentFileCard = ({
  sortableId,
  url,
  name,
  showVideo,
  downloading,
  onDownload,
  onDelete,
  onPreview,
  deleteDisabled,
  reorderDisabled,
  downloadAriaLabel,
}) => {
  const classes = useStyles();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [mediaReady, setMediaReady] = useState(() => !url);
  const deleteConfirmAnchorRef = useRef(null);
  const imageRef = useRef(null);
  const videoRef = useRef(null);

  const hasMedia = Boolean(url && url.trim());
  const showPlaceholder = !hasMedia && !showVideo;
  const canDrag = mediaReady && !reorderDisabled;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled: !canDrag,
  });
  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : undefined,
    touchAction: canDrag ? 'none' : undefined,
  };

  const markMediaReady = useCallback(() => {
    setMediaReady(true);
  }, []);

  useEffect(() => {
    setDeleteConfirmOpen(false);
    setMediaReady(!hasMedia);
  }, [url, showVideo, hasMedia]);

  useEffect(() => {
    if (mediaReady || !hasMedia) return;
    if (!showVideo) {
      const image = imageRef.current;
      if (image && image.complete && image.naturalWidth > 0) {
        setMediaReady(true);
      }
      return;
    }
    const video = videoRef.current;
    if (video && video.readyState >= 2) {
      setMediaReady(true);
    }
  }, [mediaReady, showVideo, url, hasMedia]);

  useEffect(() => {
    if (deleteDisabled && deleteConfirmOpen) {
      setDeleteConfirmOpen(false);
    }
  }, [deleteConfirmOpen, deleteDisabled]);

  useEffect(() => {
    if (!deleteConfirmOpen) return undefined;
    const onPointerDown = event => {
      if (
        deleteConfirmAnchorRef.current &&
        !deleteConfirmAnchorRef.current.contains(event.target)
      ) {
        setDeleteConfirmOpen(false);
      }
    };
    const onKeyDown = event => {
      if (event.key === 'Escape') setDeleteConfirmOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [deleteConfirmOpen]);

  const stopDrag = event => {
    event.stopPropagation();
  };

  return (
    <Box
      ref={setNodeRef}
      className={clsx(
        classes.row,
        canDrag && !isDragging && classes.grabbable,
        isDragging && classes.dragging,
      )}
      style={rowStyle}
      {...attributes}
      {...(canDrag ? listeners : {})}
    >
      {mediaReady && !reorderDisabled && (
        <Box className={classes.dragHandle} aria-hidden>
          <ArrowsNav color="var(--content-primary)" />
        </Box>
      )}
      <Box className={classes.card}>
        {mediaReady && (
          <>
            <Box className={classes.actions}>
              {hasMedia && (
                <button
                  type="button"
                  className={classes.actionButton}
                  title="Preview"
                  aria-label="Preview"
                  disabled={downloading}
                  onPointerDown={stopDrag}
                  onClick={onPreview}
                >
                  <Eye
                    className={classes.icon}
                    color="var(--content-inverted)"
                  />
                </button>
              )}
              <button
                type="button"
                className={classes.actionButton}
                title={downloadAriaLabel || 'Download'}
                aria-label={downloadAriaLabel || 'Download file'}
                disabled={downloading}
                onPointerDown={stopDrag}
                onClick={onDownload}
              >
                <Download className={classes.icon} color="FFFFFF" />
              </button>
            </Box>
            <Box
              ref={deleteConfirmAnchorRef}
              className={classes.deleteAnchor}
              onPointerDown={stopDrag}
            >
              <button
                type="button"
                className={classes.deleteButton}
                title="Delete"
                aria-label="Delete file"
                disabled={deleteDisabled}
                onClick={() => {
                  if (!deleteDisabled) setDeleteConfirmOpen(open => !open);
                }}
              >
                <Trash color="var(--content-inverted)" />
              </button>
              {deleteConfirmOpen && (
                <Box className={classes.confirm} role="dialog">
                  <Box className={classes.confirmMessage}>
                    Do you want to delete this file?
                  </Box>
                  <Box className={classes.confirmActions}>
                    <button
                      type="button"
                      className={`${classes.confirmButton} ${classes.cancelButton}`}
                      onClick={() => setDeleteConfirmOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`${classes.confirmButton} ${classes.confirmDeleteButton}`}
                      disabled={deleteDisabled}
                      onClick={() => {
                        setDeleteConfirmOpen(false);
                        onDelete();
                      }}
                    >
                      Delete
                    </button>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}

        <Box className={classes.mediaWrap}>
          {!mediaReady && (
            <Box className={classes.loader} aria-hidden>
              <CircularProgress size={24} style={{ color: '#171C2666' }} />
            </Box>
          )}
          {showPlaceholder ? (
            <Box className={classes.placeholder} aria-hidden>
              <Panorama color="var(--content-secondary)" />
            </Box>
          ) : !showVideo ? (
            <img
              ref={imageRef}
              src={url}
              alt={name || 'Content'}
              className={clsx(
                classes.media,
                !mediaReady && classes.mediaHidden,
              )}
              draggable={false}
              onLoad={markMediaReady}
              onError={markMediaReady}
            />
          ) : (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              ref={videoRef}
              src={url}
              className={clsx(
                classes.media,
                !mediaReady && classes.mediaHidden,
              )}
              muted
              playsInline
              preload="auto"
              controls={mediaReady}
              draggable={false}
              onLoadedData={markMediaReady}
              onError={markMediaReady}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

ContentFileCard.propTypes = {
  sortableId: PropTypes.string.isRequired,
  url: PropTypes.string,
  name: PropTypes.string,
  showVideo: PropTypes.bool,
  downloading: PropTypes.bool,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  deleteDisabled: PropTypes.bool,
  reorderDisabled: PropTypes.bool,
  downloadAriaLabel: PropTypes.string,
};

ContentFileCard.defaultProps = {
  url: '',
  name: '',
  showVideo: false,
  downloading: false,
  deleteDisabled: false,
  reorderDisabled: false,
  downloadAriaLabel: undefined,
};
